<template>
  <BaseClearButton @click="docsModal?.$el?.present()" color="light" size="small">
    <i class="icon-file text-blue-500 mr-1"></i>
    <span class="text-blue-500 font-medium mr-1">{{ docs?.length || 0 }}</span>
    <span>{{ buttonText }}{{ docs?.length === 1 ? '' : 's' }}</span>
  </BaseClearButton>

  <IonModal ref="docsModal">
    <IonHeader class="ion-no-border px-[10px]">
      <IonToolbar color="white" class="border-b-2 border-gray-300">
        <IonTitle color="medium">{{ modalHeaderText }}</IonTitle>
        <IonButtons slot="end">
          <IconButton icon="icon-x" @click="docsModal?.$el?.dismiss()"></IconButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>
    <IonContent>
      <IonRefresher :mode="refresherMode" v-if="props.onRefresh" slot="fixed" @ion-refresh="props.onRefresh">
        <IonRefresherContent :mode="refresherMode"></IonRefresherContent>
      </IonRefresher>
      <IonList lines="none" class="p-2">
        <DocRow v-for="d of docs" :doc="d" @doc-clicked="docClicked" @delete-clicked="removeDoc(d.name)"></DocRow>
      </IonList>
      <IonModal ref="docDetailsModal">
        <IonHeader class="ion-no-border px-[10px]">
          <IonToolbar color="white" class="border-b-2 border-gray-300">
            <IonButtons slot="start">
              <BaseClearButton @click="externalLink()"><i class="icon-external-link"></i> </BaseClearButton>
            </IonButtons>
            <IonTitle color="medium">{{ selectedDoc?.doc.title }}</IonTitle>
            <IonButtons slot="end">
              <BaseClearButton @click="docDetailsModal?.$el?.dismiss()"><i class="icon-x"></i></BaseClearButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent :scroll-y="false">
          <iframe class="h-full w-full" :src="selectedDoc?.url" frameborder="0"></iframe>
        </IonContent>
      </IonModal>
    </IonContent>
    <IonFooter color="white" class="ion-no-border p-[10px]">
      <IonToolbar color="white" class="border-t-2 border-gray-300">
        <input ref="fileInput" class="hidden" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" />
        <LightBlockButton :disabled="loading" @click="onUploadDocClicked">
          <div class="flex items-center" v-if="!loading">
            <i class="icon-upload mr-2 text-blue-500"></i>
            Upload Doc
          </div>
          <div v-else class="flex items-center">
            <IonSpinner class="mr-1" name="lines-small" color="primary"></IonSpinner>
            Uploading
          </div>
        </LightBlockButton>
      </IonToolbar>
    </IonFooter>
  </IonModal>
</template>

<script setup lang="ts">
import { IDocument } from '@ncss/models';
import BaseClearButton from '../forms/BaseClearButton.vue';
import {
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonList,
  IonModal,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from '@ionic/vue';
import IconButton from '../forms/IconButton.vue';
import { ref, useTemplateRef } from 'vue';
import { useToast } from '@/composables/use-toast';
import DocRow from '@/components/docs/DocRow.vue';
import LightBlockButton from '../forms/LightBlockButton.vue';
import { useRefresherMode } from '@/composables/use-refresher-mode';

const props = withDefaults(
  defineProps<{
    docs?: IDocument[];
    onRefresh?: (e?: CustomEvent) => Promise<void>;
    onAddDoc?: (name: string, base64: string) => Promise<void>;
    onRemoveDoc?: (name: string) => Promise<void>;
    buttonText?: string;
    modalHeaderText?: string;
  }>(),
  { buttonText: 'Doc', modalHeaderText: 'Documents' }
);

const docsModal = useTemplateRef('docsModal');
const { successToast, errorToast } = useToast();
const fileInput = ref();
const loading = ref(false);
const selectedDoc = ref<{ doc: IDocument; url: string } | undefined>();
const docDetailsModal = ref<InstanceType<typeof IonModal>>();
const { mode: refresherMode } = useRefresherMode();

function docClicked(doc: IDocument, url: string) {
  selectedDoc.value = { doc, url };
  docDetailsModal.value?.$el?.present();
}

function externalLink() {
  if (selectedDoc.value?.url) {
    window.open(selectedDoc.value?.url, '_self');
  }
}

async function removeDoc(name: string) {
  if (props.onRemoveDoc) {
    loading.value = true;
    try {
      await props.onRemoveDoc(name);
      successToast('Document removed!');
    } catch (e) {
      errorToast('Failed to remove document');
    }
    loading.value = false;
  }
}

/**
 * Function that handles searching for a document and uploading it.
 *
 * *Note: Requires the `onAddDoc` prop to be set.*
 */
async function onUploadDocClicked() {
  if (!props.onAddDoc) {
    return;
  }
  fileInput.value.click();
  fileInput.value.onchange = async () => {
    loading.value = true;
    try {
      const file: File = fileInput.value.files[0];
      const base64 = await toBase64(file);
      if (typeof base64 === 'string') {
        await props.onAddDoc!(file.name, base64);
        successToast('Document Uploaded!');
      } else {
        errorToast('Oops, upload failed');
      }
    } catch (e) {
      errorToast('Oops, upload failed');
    }
    loading.value = false;
  };
}

function toBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
  });
}
</script>

<style scoped></style>
